import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { msalInstance } from "./auth/msalInstance";
import { acquireIdToken, AUTH_REQUIRED_EVENT } from "./auth/acquireIdToken";
import LoginPage from "./pages/LoginPage";
import SessionExpiredPage from "./pages/SessionExpiredPage";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import RostersPage from "./pages/RostersPage";
import RosterTemplatesPage from "./pages/RosterTemplatesPage";
import CalendarPage from "./pages/CalendarPage";
import { api } from "./lib/api";
import type { User } from "@domaddress/shared";

type AuthPhase = "loading" | "ready" | "signed-out" | "needs-interactive";

function AppRoutes() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  /** Startup en de terugkeer van Microsoft zijn nog bezig. Dan is "niet ingelogd" nog niet waar. */
  const msalReady =
    inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect;
  const [user, setUser] = useState<User | null>(null);
  const [phase, setPhase] = useState<AuthPhase>("loading");

  useEffect(() => {
    const onAuthRequired = () => setPhase("needs-interactive");
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  useEffect(() => {
    if (!msalReady) return;
    if (!isAuthenticated || !account) {
      setUser(null);
      setPhase("signed-out");
      return;
    }
    let cancelled = false;
    setPhase("loading");
    void (async () => {
      const result = await acquireIdToken({ allowInteractive: true });
      if (cancelled || result.redirecting) return;
      if (!result.token) {
        setUser(null);
        setPhase("needs-interactive");
        return;
      }
      try {
        const me = await api.get<{ data: User }>("/api/me");
        if (cancelled) return;
        setUser(me.data.data);
        setPhase("ready");
      } catch {
        if (cancelled) return;
        setUser(null);
        setPhase("needs-interactive");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [msalReady, isAuthenticated, account]);

  /**
   * Houd het id-token geldig. De timer dekt normaal gebruik.
   * visibilitychange dekt een tab die uren op de achtergrond stond.
   */
  useEffect(() => {
    if (phase !== "ready" || !account) return;

    async function tryRefresh(allowInteractive: boolean) {
      const result = await acquireIdToken({ allowInteractive });
      if (!result.token && !result.redirecting) {
        setPhase("needs-interactive");
      }
    }

    const id = window.setInterval(() => {
      void tryRefresh(false);
    }, 45 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void tryRefresh(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [phase, account]);

  if (!msalReady || phase === "loading") {
    return <div className="app-loading">Even geduld, aanmelding bij de server…</div>;
  }

  if (phase === "needs-interactive") {
    return <SessionExpiredPage />;
  }

  if (!isAuthenticated || phase === "signed-out") {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route element={<Layout user={user} />}>
        <Route index element={<HomePage />} />
        <Route path="kalender" element={<CalendarPage />} />
        <Route path="evenementen" element={<EventsPage />} />
        <Route path="evenementen/:id" element={<EventDetailPage />} />
        <Route path="roosters" element={<RostersPage />} />
        <Route path="rooster-sjablonen" element={<RosterTemplatesPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </MsalProvider>
  );
}
